import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientAPI } from '../services/api';
import SearchableDropdown from '../components/SearchableDropdown';
import { STATE_CITY_DATA, STATES_LIST, ALL_CITIES_LIST } from '../data/locations';
import './PatientForm.css';

function PatientForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    phone: '',
    email: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    },
    medicalHistory: '',
    allergies: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleStateChange = (value) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        state: value
      }
    }));
  };

  const handleCityChange = (value) => {
    let stateToSet = formData.address.state;
    if (!stateToSet) {
      for (const [state, cities] of Object.entries(STATE_CITY_DATA)) {
        if (cities.includes(value)) {
          stateToSet = state;
          break;
        }
      }
    }
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        city: value,
        ...(stateToSet ? { state: stateToSet } : {})
      }
    }));
  };

  const availableCities = formData.address.state && STATE_CITY_DATA[formData.address.state]
    ? STATE_CITY_DATA[formData.address.state]
    : ALL_CITIES_LIST;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await patientAPI.create(formData);
      setSuccess('Patient registered successfully!');

      // Reset form
      setTimeout(() => {
        setFormData({
          firstName: '',
          lastName: '',
          dateOfBirth: '',
          gender: '',
          phone: '',
          email: '',
          address: {
            street: '',
            city: '',
            state: '',
            zipCode: ''
          },
          medicalHistory: '',
          allergies: ''
        });
        setSuccess('');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to register patient');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="patient-form-container">
      <div className="patient-form-card">
        <h1 className="patient-form-title">📋 Patient Registration Form</h1>

        {error && <div className="error-message">❌ {error}</div>}
        {success && <div className="success-message">✅ {success}</div>}

        <form onSubmit={handleSubmit}>
          <section className="form-section">
            <h2>Personal Information</h2>

            <div className="form-row">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  placeholder="Enter first name"
                />
              </div>

              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Enter last name (optional)"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Date of Birth *</label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Gender *</label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="Enter phone number"
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                />
              </div>
            </div>
          </section>

          <section className="form-section">
            <h2>Address</h2>

            <div className="form-group">
              <label>Street Address</label>
              <input
                type="text"
                name="address.street"
                value={formData.address.street}
                onChange={handleChange}
                placeholder="Enter street address"
              />
            </div>

            <div className="form-row-3">
              <div className="form-group">
                <label>State</label>
                <SearchableDropdown
                  name="address.state"
                  value={formData.address.state}
                  onChange={handleStateChange}
                  options={STATES_LIST}
                  placeholder="Select or search state"
                />
              </div>

              <div className="form-group">
                <label>City</label>
                <SearchableDropdown
                  name="address.city"
                  value={formData.address.city}
                  onChange={handleCityChange}
                  options={availableCities}
                  placeholder="Select or search city"
                />
              </div>

              <div className="form-group">
                <label>Zip Code</label>
                <input
                  type="text"
                  name="address.zipCode"
                  value={formData.address.zipCode}
                  onChange={handleChange}
                  placeholder="Enter zip code"
                />
              </div>
            </div>
          </section>

          <section className="form-section">
            <h2>Medical Information</h2>

            <div className="form-group">
              <label>Medical History</label>
              <textarea
                name="medicalHistory"
                value={formData.medicalHistory}
                onChange={handleChange}
                rows="4"
                placeholder="Enter any relevant medical history..."
              />
            </div>

            <div className="form-group">
              <label>Allergies</label>
              <textarea
                name="allergies"
                value={formData.allergies}
                onChange={handleChange}
                rows="3"
                placeholder="Enter any known allergies..."
              />
            </div>
          </section>

          <div className="form-actions">
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Registering...' : 'Register Patient'}
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => navigate('/patients')}
              disabled={loading}
            >
              View All Patients
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PatientForm;
